'use client'
import { useCallback, useMemo, useRef, useState, useEffect, memo } from 'react'
import type { JSX } from 'react'
import { useModal } from '@/hooks'
import { Figure } from '../../Figure'
import { useDropdown } from '../../dropdown/useDropdown'
import { noRegister } from '../Input'
import { Div, Label, Ul, arrowIcon, optionsListVariants } from './styles'
import type { TypeSelectBase, TypeSelectOption } from './types'
import { useSelectSearch } from './useSelectSearch'

const BLUR_DELAY_MS = 200
const MIN_MATCH_LENGTH = 3

type TypeSelect = TypeSelectBase & { defaultValue?: string }

const getOptionsList = (data: TypeSelectOption[], onClickOption: (option: TypeSelectOption) => void, currentValue: string | number) => data.map((option, idx) => {
  return (
    <li key={`${option.value}-${idx}`} className={`${currentValue === option.value ? 'current' : ''}${option.store ? ` online-store${data[idx + 1]?.store ? '' : ' last-store'}` : ''}`}>
      <button type='button' onMouseDown={() => { onClickOption(option) }} data-dismiss={true}>
        {option.name}
        {option.store && <span className='online-store'>Tienda online</span>}
      </button>
    </li>
  )
})

export const Select = memo(({ options, name = 'country_code', defaultValue, label, autoComplete = 'off', placeholder, placeholderVisible = false, required = false, disabled = false, readOnly = null, onChange = null, col, register = noRegister, icon = null, className, textColor = 'text', bgColor = 'background', error, setFormValue, dropdownTextColor = null, modalId, payloadKey }: TypeSelect): JSX.Element => {
  const requireProps = typeof required === 'boolean' ? { required } : required
  const { onChange: registerOnChange, onBlur: registerOnBlur, name: registerName, ref: registerRef } = register(name, { ...requireProps })
  const { isActive, payload } = useModal(modalId)

  const [fancyValue, setFancyValue] = useState(options.filter(option => option.value === defaultValue)[0]?.name ?? '')
  const [isOptionClicked, setIsOptionClicked] = useState(false)
  const [currentValue, setCurrentValue] = useState<string | number>(defaultValue ?? '')
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { currentOptions, filterOptions, resetOptions } = useSelectSearch({ options })
  const [triggerRef, dropdownRef, expanded, onTriggerFocus, toggleExpanded] = useDropdown()

  const handleChange = e => {
    registerOnChange?.(e)
    onChange?.(e)
  }

  const handleBlur = e => {
    registerOnBlur?.(e)
  }

  const onClickOption = (option: TypeSelectOption) => {
    setIsOptionClicked(true)
    setFormValue?.(name, option.value)
    setCurrentValue(option.value)
    setFancyValue(option.name)
    toggleExpanded()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const Options = useMemo(() => getOptionsList(currentOptions, onClickOption, currentValue), [currentOptions, currentValue])

  const handleSearch = e => {
    const value = e?.target?.value
    setFancyValue(value)
    if (typeof value === 'string') filterOptions(value)
  }

  const onBlurSearch = useCallback(() => {
    if (isOptionClicked) {
      setIsOptionClicked(false)
      return
    }

    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    blurTimeoutRef.current = setTimeout(() => {
      setIsOptionClicked(false)
      const value = fancyValue
      let matchOption = null
      if (typeof value === 'string' && value.length >= MIN_MATCH_LENGTH) {
        matchOption = options.filter(option => option.name.toLowerCase().includes(value.toLowerCase()))
        if (matchOption.length === 1) matchOption = matchOption[0]
      }

      setFormValue(name, matchOption?.value ?? '')
      setCurrentValue(matchOption?.value ?? '')
      setFancyValue(matchOption?.name ?? '')
      resetOptions()
    }, BLUR_DELAY_MS)
  }, [fancyValue, isOptionClicked, name, options, resetOptions, setFormValue])

  useEffect(() => () => { if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current) }, [])

  useEffect(() => {
    let matchOption = null

    if (defaultValue) {
      matchOption = options.filter(option => option.value === defaultValue)?.[0]

      if (!matchOption) return
      setFormValue(name, matchOption.value)
      setCurrentValue(matchOption.value)
      setFancyValue(matchOption.name)
    }
  }, [defaultValue, options, name, setFormValue])

  useEffect(() => {
    // This useEffect will only run if the <Select /> is inside a modal, resetting the
    // selection every time the modal opens and prefilling it from the payload when asked to.
    if (!modalId) return
    if (!isActive) return

    const matchOption = payloadKey ? options.filter(option => option.value === payload?.[payloadKey])?.[0] : null
    setFormValue(name, matchOption?.value ?? '')
    setCurrentValue(matchOption?.value ?? '')
    setFancyValue(matchOption?.name ?? '')
  }, [isActive, payload, payloadKey, options, name, setFormValue, modalId])

  return (
    <Div ref={triggerRef} $col={col} $readOnly={readOnly}>
      <input type='hidden' name={registerName} required={required} aria-invalid={!!error}
        {...registerRef && { ref: registerRef }}
        {...defaultValue && { defaultValue }}
        {...handleChange && { onChange: handleChange }}
        {...handleBlur && { onBlur: handleBlur }}
      />
      <Label $hasContent={!!fancyValue} $placeholderVisible={placeholderVisible} $textColor={textColor} $bgColor={bgColor} {...className && { className }}>
        <input type='text' name={`${name}_searcher`} disabled={disabled} readOnly={readOnly} placeholder={placeholder ?? label} onChange={handleSearch} onFocus={onTriggerFocus} onBlur={onBlurSearch} value={fancyValue} autoComplete={autoComplete} />
        {label && <span>{label}</span>}
        {icon ? icon : <Figure media={arrowIcon} fill={false} lazy={true} className='chevron' />}
      </Label>
      {!readOnly && <Ul ref={dropdownRef} initial='collapsed' variants={optionsListVariants} animate={expanded ? 'expanded' : 'collapsed'} $textColor={textColor} $dropdownTextColor={dropdownTextColor}>
        {Options}
      </Ul>}
    </Div>
  )
})

Select.displayName = 'Select'
